import { ERROR_CODES } from './http.data.js';
import { HttpError } from './http.error.js';
import { __ } from './i18n.js';
import { SessionStorage } from './session-storage.js';

class Http {
    static setDefaultHeader(header) {
        if (typeof header !== "object") {
            return;
        }
        Http.defaultHeader = Object.fromEntries(Object.entries({
            ...Http.defaultHeader,
            ...header
        }).filter(([_, value])=>value !== undefined && value !== null));
    }
    static setPostProcess(postProcess) {
        Http.postProcess = postProcess;
    }
    static setRequestJsonParser(parser) {
        Http.requestJsonParser = parser;
    }
    static setResponseJsonParser(parser) {
        Http.responseJsonParser = parser;
    }
    static setErrorParser(errorParser) {
        Http.errorParser = errorParser;
    }
    static setDummyMode(dummyMode) {
        Http.dummyMode = dummyMode;
    }
    static async get(url, config) {
        const fullUrl = url + ((url.includes("?") ? "&" : "?") + new URLSearchParams(Http.removeEmpty(config?.searchParams)).toString()).replace(/^\?$/, "");
        if (Http.cachePromise.has(fullUrl)) {
            return Http.cachePromise.get(fullUrl);
        }
        if (config?.cacheExpiry) {
            const localData = SessionStorage.get(fullUrl);
            if (localData) {
                const { ttl = 0, value } = JSON.parse(localData);
                if (Date.now() > ttl) {
                    SessionStorage.remove(fullUrl);
                } else {
                    return Promise.resolve(value);
                }
            }
        }
        const promise = Http.fetch(fullUrl, "GET", undefined, config?.init).then((result)=>{
            if (config?.cacheExpiry) {
                SessionStorage.set(fullUrl, JSON.stringify({
                    value: result,
                    ttl: Date.now() + config.cacheExpiry
                }));
            }
            Http.cachePromise.delete(fullUrl);
            return result;
        }).catch((reason)=>{
            Http.cachePromise.delete(fullUrl);
            SessionStorage.remove(fullUrl);
            throw reason;
        });
        Http.cachePromise.set(fullUrl, promise);
        return promise;
    }
    static async getWithCache(url, searchParams, init) {
        return Http.get(url, {
            cacheExpiry: 3 * 60 * 60 * 1000,
            searchParams,
            init
        });
    }
    static async post(url, jsonData, init) {
        return Http.fetch(url, "POST", jsonData, init);
    }
    static async postFormData(url, formData) {
        return Http.fetch(url, "POST", undefined, {
            body: formData
        });
    }
    static async put(url, jsonData, init) {
        return Http.fetch(url, "PUT", jsonData, init);
    }
    static async patch(url, jsonData, init) {
        return Http.fetch(url, "PATCH", jsonData, init);
    }
    static async delete(url, jsonData, init) {
        return Http.fetch(url, "DELETE", jsonData, init);
    }
    static async fetch(url, method, jsonData, init = {}) {
        const { headers = {}, body } = init || {};
        const settings = {
            ...init,
            method
        };
        if (Http.dummyMode) {
            return Promise.reject();
        }
        if (jsonData) {
            headers["Content-Type"] = "application/json";
            settings.body = Http.requestJsonParser(jsonData);
        } else if (body) {
            settings.body = body;
        }
        settings.headers = new Headers({
            ...Http.defaultHeader,
            ...headers
        });
        const cb = ()=>fetch(url, settings).then((response)=>{
                const contentType = response.headers.get("content-type");
                const contentLength = response.headers.get("content-length");
                if (contentLength !== "0" && contentType) {
                    let promise;
                    if (contentType.includes("application/json")) {
                        promise = response.text().then((text)=>Http.responseJsonParser(text)).catch(()=>{
                            throw new HttpError(__("Hiba az adatok feldolgozása közben!"), {
                                errorCode: "PARSE_ERROR",
                                status: response.status,
                                request: jsonData,
                                body: undefined,
                                response
                            });
                        });
                    }
                    if (contentType.includes("text/html") || contentType.includes("text/plain")) {
                        promise = response.text();
                    }
                    if (promise) {
                        return promise.then((data)=>{
                            if (response.ok) {
                                return data;
                            }
                            throw Http.errorParser(jsonData, response, data);
                        }).catch((reason)=>{
                            if (reason.name === "HttpError") {
                                throw reason;
                            }
                            throw Http.errorParser(jsonData, response, reason);
                        });
                    }
                }
                if (response.ok) {
                    return response;
                }
                throw Http.errorParser(jsonData, response, response);
            });
        return Http.postProcess(url, init, cb);
    }
    static dummyResponse(response, ms) {
        return new Promise((resolve)=>{
            setTimeout(()=>{
                resolve(response);
            }, ms);
        });
    }
    static removeEmpty(obj) {
        const newObj = {};
        if (!obj) {
            return obj;
        }
        Object.keys(obj).forEach((key)=>{
            if (obj[key] === Object(obj[key])) newObj[key] = Http.removeEmpty(obj[key]);
            else if (obj[key] !== undefined) newObj[key] = obj[key];
        });
        return newObj;
    }
}
Http.EXPIRY_SHORT = 3000;
Http.EXPIRY_MIDDLE = 60 * 1000;
Http.EXPIRY_LONG = 60 * 60 * 1000;
Http.EXPIRY_VERY_LONG = Http.EXPIRY_LONG * 12;
Http.defaultHeader = {};
Http.cachePromise = new Map();
Http.dummyMode = false;
Http.postProcess = (_url, _init, cb)=>cb();
Http.requestJsonParser = (data)=>JSON.stringify(data);
Http.responseJsonParser = (data)=>JSON.parse(data);
Http.errorParser = (request, response, body)=>{
    const errorCode = ERROR_CODES[response?.status].code || "UNKNOWN_ERROR";
    const message = ERROR_CODES[response?.status].message || __("Ismeretlen hiba történt!");
    return new HttpError(body?.message || response?.statusText || message, {
        errorCode: body?.error || body?.errorCode || body?.type || errorCode,
        status: body?.code || body?.status || response?.status || 500,
        request,
        body,
        response
    });
};

export { Http };
//# sourceMappingURL=http.js.map
